import React from "react";
import HintBlocks, { HintCard, type HintTone } from "../../components/HintBlocks";
import type { Category, FormStructure } from "../../types/form";
// AI form assistant temporarily disabled — re-enable import + HintsAiChat blocks below when ready.
// import HintsAiChat from "./HintsAiChat";

interface HintItem {
  title?: string;
  content?: string;
  type?: string;
  items?: string[];
  text?: string;
  message?: string;
  [key: string]: any;
}

export interface ActiveHint {
  title?: string;
  content: string;
}

interface HintsProps {
  activeSection?: (Category & { title?: string }) | {
    title?: string;
    name?: string;
    description?: string;
    instructions?: string;
    hints?: HintItem[] | string | any;
  };
  activeHint?: ActiveHint | null;
  formData: Record<string, unknown>;
  formStructure: FormStructure | null;
  formId: number | undefined;
  selectCategoryById: (categoryId: number) => boolean;
  navigateToCategoryContainingQuestion: (questionId: number) => boolean;
  onEnsureHintsOpen?: () => void;
}

function normalizeHints(raw: unknown): HintItem[] {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw;
  if (typeof raw === "string") return [{ content: raw }];
  if (typeof raw === "object") return [raw as HintItem];
  return [];
}

function hintItemBody(hint: HintItem): string {
  const parts: string[] = [];
  if (hint.content) parts.push(hint.content);
  if (hint.text) parts.push(hint.text);
  if (hint.message) parts.push(hint.message);
  if (hint.items && Array.isArray(hint.items) && hint.items.length > 0) {
    parts.push(hint.items.map((item) => `- ${item}`).join("\n"));
  }
  return parts.filter(Boolean).join("\n\n");
}

function toneForHintType(type?: string): HintTone {
  if (type === "tip" || type === "tips") return "tip";
  if (type === "warning") return "warning";
  if (type === "important") return "important";
  return "section";
}

const Hints: React.FC<HintsProps> = ({
  activeSection,
  activeHint,
}) => {
  if (!activeSection && !activeHint?.content) {
    return (
      <div
        data-testid="hints-empty"
        className="flex h-full min-h-0 flex-1 items-center justify-center p-2"
      >
        <p className="text-[#1B365D] text-sm text-center">
          Select a section to view hints and instructions.
        </p>
      </div>
    );
  }

  const hintsArray = normalizeHints(activeSection?.hints);
  const sectionTitle =
    activeSection?.title || activeSection?.name || "Current Section";
  const instructionsContent =
    activeSection?.instructions || activeSection?.description || "";
  const hasInstructions = Boolean(instructionsContent.trim());
  const hasStructuredHints = hintsArray.length > 0;
  const hasSectionCopy = hasInstructions || hasStructuredHints;
  const hasQuestionHint = Boolean(activeHint?.content);

  if (!hasSectionCopy && !hasQuestionHint) {
    return (
      <div className="flex h-full min-h-0 flex-1 flex-col gap-3">
        <SectionIdentity title={sectionTitle} />
        <div
          data-testid="hints-empty"
          className="flex min-h-0 flex-1 flex-col justify-center"
        >
          <p className="text-[#1B365D] text-sm">
            No hints or instructions available for this section.
          </p>
        </div>
      </div>
    );
  }

  const questionMaxHeight = hasSectionCopy
    ? "max-h-[min(22rem,50vh)]"
    : undefined;
  const sectionMaxHeight = hasQuestionHint
    ? "max-h-[min(16rem,36vh)]"
    : undefined;

  return (
    <div
      data-testid="hints-panel"
      className="flex h-full min-h-0 flex-1 flex-col gap-3"
    >
      {activeSection ? <SectionIdentity title={sectionTitle} /> : null}

      <div className="min-h-0 flex-1 overflow-y-auto scrollbar-thin scrollbar-thumb-slate-400 pr-1 flex flex-col gap-3">
        {hasQuestionHint && activeHint ? (
          <HintCard
            testId="hints-question"
            eyebrow="This question"
            title={activeHint.title}
            tone="question"
            maxHeightClass={questionMaxHeight}
          >
            <HintBlocks text={activeHint.content} />
          </HintCard>
        ) : null}

        {hasSectionCopy ? (
          <div data-testid="hints-section" className="flex flex-col gap-3">
            {hasInstructions ? (
              <HintCard
                eyebrow="About this section"
                tone="section"
                maxHeightClass={sectionMaxHeight}
              >
                <HintBlocks text={instructionsContent} />
              </HintCard>
            ) : null}
            {hasStructuredHints
              ? hintsArray.map((hint, index) => (
                  <HintCard
                    key={index}
                    eyebrow={hint.title ? undefined : "Section hint"}
                    title={hint.title}
                    tone={toneForHintType(hint.type)}
                    maxHeightClass={hasQuestionHint ? sectionMaxHeight : undefined}
                  >
                    <HintBlocks text={hintItemBody(hint)} />
                  </HintCard>
                ))
              : null}
          </div>
        ) : null}
      </div>

      {/* AI form assistant (HintsAiChat) — disabled for now
      {activeSection && isCategoryForAi(activeSection) ? (
        <HintsAiChat ... />
      ) : null}
      */}
    </div>
  );
};

const SectionIdentity = ({ title }: { title: string }) => (
  <div className="shrink-0 rounded-lg border border-[#E5E7EB] bg-[#F9FAFB] px-3 py-2">
    <div className="text-[11px] font-bold uppercase tracking-[0.04em] text-[#6B7280]">
      Section
    </div>
    <div className="text-sm font-semibold text-[#1B365D] leading-snug">
      {title}
    </div>
  </div>
);

export default Hints;
